/*
 * Due Diligence Workbench — calculation engines.
 *
 * Three engines, all admin-tunable:
 *   1. Buyer's baseline adaptation tax  (calcBaselineTax)
 *   2. Per-vendor projected adaptation tax  (projectVendorTax)
 *   3. Adoption confidence + value at risk  (adoptionConfidence, valueAtRisk)
 *
 * Ported verbatim from artifacts/prototypes/due-diligence-workbench-v1.html
 * §6 (calcTax, projectVendorTax) and §10 (adoptionConfidence, valueAtRisk).
 * Do NOT redesign the math without product/business sign-off.
 *
 * Constants come from admin config (api.getPublishedConfig('ddw')).
 * Fall back to prototype defaults if no published config exists.
 */

import {
  type AdoptionQuestion,
  type CalcConstants,
  type DdwConfig,
  type ProjectionConstants,
  type ScaleOption,
  type SpendTier,
  DEFAULT_DDW_CONFIG,
  FLEX_MATCH_OPTIONS,
} from './config';
import type {
  BaselineTax,
  DdwLens,
  DdwScore,
  DdwVendor,
  VendorProjection,
  VendorSpend,
  FlexibilityLevel,
} from './types';

/** Buyer's baseline adaptation tax per year (no vendor in the picture). */
export function calcBaselineTax(lens: DdwLens, calc: CalcConstants = DEFAULT_DDW_CONFIG.calc): BaselineTax {
  const t = lens.tax;
  const routineCost = t.routine * calc.hoursPerRoutineChange * calc.hourlyCost;
  const workaroundCost = t.workarounds * calc.workaroundHrsPerMonth * 12 * calc.hourlyCost;
  const majorEventCost = t.major * calc.hoursPerMajorEvent * calc.hourlyCost;
  const dragCost =
    t.major *
    calc.productivityDragPct *
    calc.teamMedian *
    calc.dragHoursPerMonth *
    calc.dragMonths *
    calc.hourlyCost;
  const total = routineCost + workaroundCost + majorEventCost + dragCost;
  return { routineCost, workaroundCost, majorEventCost, dragCost, total };
}

/**
 * Projected adaptation tax for a buyer using a specific vendor. Uses
 * the vendor's flex-match tier as the routine `hoursPerChange`
 * multiplier (toolkit p.30: 4 / 16 / 60 / 160 hrs by tier), then adds
 * scale-out and scale-up penalties driven by lens flexibility level.
 */
export function projectVendorTax(
  lens: DdwLens,
  score: DdwScore,
  config: DdwConfig = DEFAULT_DDW_CONFIG,
): VendorProjection {
  const calc = config.calc;
  const projection: ProjectionConstants = config.projection;
  const t = lens.tax;
  const flexLevel: FlexibilityLevel = lens.flexibility ?? 'medium';

  const flex = FLEX_MATCH_OPTIONS.find((f) => f.key === score.flexMatch);
  const scaleOut: ScaleOption | undefined = config.scaleOut.find((s) => s.key === score.scaleOut);
  const scaleUp: ScaleOption | undefined = config.scaleUp.find((s) => s.key === score.scaleUp);

  const hoursPerChange = flex ? flex.hoursPerChange : calc.hoursPerRoutineChange;
  const routineCost = t.routine * hoursPerChange * calc.hourlyCost;

  const expansions = projection.expectedExpansionsByFlex[flexLevel] ?? 0;
  const scaleOutPenalty = scaleOut
    ? expansions * scaleOut.penaltyMult * calc.hoursPerMajorEvent * calc.hourlyCost
    : 0;

  const growth = projection.growthIntensityByFlex[flexLevel] ?? 0;
  const scaleUpPenalty = scaleUp
    ? scaleUp.penaltyMult * growth * calc.hoursPerMajorEvent * calc.hourlyCost
    : 0;

  const workaroundCost = t.workarounds * calc.workaroundHrsPerMonth * 12 * calc.hourlyCost;
  const majorEventCost = t.major * calc.hoursPerMajorEvent * calc.hourlyCost;
  const dragCost =
    t.major *
    calc.productivityDragPct *
    calc.teamMedian *
    calc.dragHoursPerMonth *
    calc.dragMonths *
    calc.hourlyCost;

  const total = routineCost + scaleOutPenalty + scaleUpPenalty + workaroundCost + majorEventCost + dragCost;
  return { routineCost, scaleOutPenalty, scaleUpPenalty, workaroundCost, majorEventCost, dragCost, total };
}

/**
 * Adoption confidence (0–100). Sum of selected adoption-question pts
 * divided by max possible pts (20 per question).
 */
export function adoptionConfidence(
  score: DdwScore | null | undefined,
  questions: ReadonlyArray<AdoptionQuestion> = DEFAULT_DDW_CONFIG.adoptionQuestions,
): number {
  if (!score || !score.adoption) return 0;
  let pts = 0;
  let max = 0;
  for (const q of questions) {
    max += 20;
    const ans = score.adoption[q.key];
    const opt = q.options.find((o) => o.key === ans);
    if (opt) pts += opt.pts;
  }
  return max > 0 ? Math.round((pts / max) * 100) : 0;
}

/** Vendor's projected 18-month spend = license + internal adaptation. */
export function vendorSpend18mo(
  lens: DdwLens,
  score: DdwScore,
  config: DdwConfig = DEFAULT_DDW_CONFIG,
): VendorSpend {
  const tiers = config.spendTiers;
  const tier: SpendTier = tiers.find((t) => t.key === score.spendTier) ?? tiers[1] ?? tiers[0];
  const license18mo = tier.annual * 1.5;
  const projected = projectVendorTax(lens, score, config);
  const internalAdaptation18mo = projected.total * 1.5;
  return { license18mo, internalAdaptation18mo, total: license18mo + internalAdaptation18mo };
}

/**
 * Value at risk over 18 months = projected spend × (1 − adoption confidence).
 * The headline number that anchors the per-vendor risk view.
 */
export function valueAtRisk(
  lens: DdwLens,
  score: DdwScore,
  config: DdwConfig = DEFAULT_DDW_CONFIG,
): number {
  const spend = vendorSpend18mo(lens, score, config).total;
  const conf = adoptionConfidence(score, config.adoptionQuestions) / 100;
  return Math.round(spend * (1 - conf));
}

// ─── Convenience wrappers — vendor objects in state ──────────

export function projectionForVendor(
  lens: DdwLens,
  vendor: DdwVendor,
  config: DdwConfig = DEFAULT_DDW_CONFIG,
): VendorProjection | null {
  if (!vendor.score) return null;
  return projectVendorTax(lens, vendor.score, config);
}

export function vendorTaxDelta(lens: DdwLens, vendor: DdwVendor, config: DdwConfig = DEFAULT_DDW_CONFIG): number {
  if (!vendor.score) return 0;
  const baseline = lens.tax.calculatedAnnual || 0;
  const projected = projectVendorTax(lens, vendor.score, config).total;
  return projected - baseline;
}

// ─── Formatting ──────────────────────────────────────────────

export function fmtMoney(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export function truncate(s: string, n: number): string {
  s = String(s ?? '');
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// ─── Red flag detection ──────────────────────────────────────

export function findRedFlagHits(text: string, words: ReadonlyArray<string>): string[] {
  const lower = (text || '').toLowerCase();
  return words.filter((w) => {
    const word = w.toLowerCase();
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('\\b' + escaped + '\\b').test(lower);
  });
}
